import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../lib/api';
import { extractCodeFromResponse } from '../lib/codeUtils';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../components/ui/resizable';
import {
    Send, ArrowLeft, Plus, Loader2, Bot, User, RefreshCw, Eye,
    FolderTree, Check, AlertCircle, Code2, Download, Paperclip,
    Image as ImageIcon, History, MessageSquare, Trash2, Clock,
    FileText, Github, Brain, X, Rocket, Save, CheckCircle, ExternalLink
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import GitHubModal from '../components/GitHubModal';
import { FileTreePanel, CodeEditorPanel, PreviewPanel, ChatHistoryPanel } from '../components/editor';

// Markdown components for AI responses
const markdownComponents = {
    code({ inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');
        return !inline && match ? (
            <div className="my-2 max-w-full overflow-hidden">
                <div className="overflow-x-auto">
                    <SyntaxHighlighter 
                        style={oneLight} 
                        language={match[1]} 
                        PreTag="div" 
                        className="rounded-md text-xs"
                        customStyle={{ margin: 0, fontSize: '11px' }}
                        wrapLongLines={false}
                        {...props}
                    >
                        {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                </div>
            </div>
        ) : (
            <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-xs break-all" {...props}>{children}</code>
        );
    },
    pre: ({ children }) => <pre className="overflow-x-auto max-w-full my-2 p-0 bg-transparent">{children}</pre>,
    p: ({ children }) => <p className="mb-2 last:mb-0 break-words">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
    li: ({ children }) => <li className="text-sm break-words">{children}</li>,
    h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
    blockquote: ({ children }) => <blockquote className="border-l-2 border-gray-300 pl-3 italic text-gray-600 my-2">{children}</blockquote>,
    a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{children}</a>,
};

// Helper function to parse error message and extract log filename
const parseErrorWithLog = (errorDetail) => {
    if (!errorDetail) return { message: 'Failed to generate code', logFile: null };
    
    const parts = errorDetail.split('|LOG:');
    if (parts.length === 2) {
        return {
            message: parts[0],
            logFile: parts[1]
        };
    }
    return { message: errorDetail, logFile: null };
};

// Helper function to create error message content with log link
const createErrorContent = (errorDetail, backendUrl) => {
    const { message, logFile } = parseErrorWithLog(errorDetail);
    if (logFile) {
        const logUrl = `${backendUrl}/api/error-logs/${logFile}`;
        return `Error: ${message}\n\n📋 [View Error Log](${logUrl})`;
    }
    return `Error: ${message}`;
};

const EditorPage = () => {
    const { projectId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Project state
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeFile, setActiveFile] = useState(null);
    const [openFiles, setOpenFiles] = useState([]);
    const [fileContents, setFileContents] = useState({});
    const [unsavedChanges, setUnsavedChanges] = useState({});
    
    // UI state
    const [viewMode, setViewMode] = useState('preview');
    const [leftPanelMode, setLeftPanelMode] = useState('chat');
    
    // Chat state
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [selectedModel, setSelectedModel] = useState('claude');
    const [generating, setGenerating] = useState(false);
    const [models, setModels] = useState([]);
    const [thinkingEnabled, setThinkingEnabled] = useState(false);
    const messagesEndRef = useRef(null);
    
    // Plan state
    const [pendingPlan, setPendingPlan] = useState(null);
    const [editablePlan, setEditablePlan] = useState('');
    
    // Chat history
    const [chatHistory, setChatHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // Preview & actions state
    const [previewKey, setPreviewKey] = useState(0);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [showGitHubModal, setShowGitHubModal] = useState(false);
    
    // Deploy & Finalize state
    const [deploying, setDeploying] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [finalizationReport, setFinalizationReport] = useState(null);
    const [showFinalizationModal, setShowFinalizationModal] = useState(false);

    // GitHub OAuth callback
    useEffect(() => {
        const code = searchParams.get('code');
        if (code) {
            (async () => {
                try {
                    const { data } = await apiService.handleGitHubCallback(code, searchParams.get('state'));
                    if (data.access_token) {
                        await apiService.connectGitHub({ github_token: data.access_token, github_user: data.github_user });
                        window.history.replaceState({}, '', window.location.pathname);
                        setShowGitHubModal(true);
                    }
                } catch (error) {
                    console.error('GitHub callback error:', error);
                }
            })();
        }
    }, [searchParams]);

    // Fetch project and models
    const fetchProject = useCallback(async () => {
        try {
            const { data } = await apiService.getProject(projectId);
            setProject(data);
            setFileContents(data.files || {});
            const files = Object.keys(data.files || {});
            
            // Find App.jsx or App.js to open (check multiple possible locations)
            const appPaths = [
                'App.jsx', 'App.js',
                'src/App.jsx', 'src/App.js',
                'frontend/src/App.jsx', 'frontend/src/App.js'
            ];
            
            let appFile = null;
            for (const path of appPaths) {
                if (files.includes(path)) {
                    appFile = path;
                    break;
                }
            }
            
            if (appFile) {
                setActiveFile(appFile);
                setOpenFiles([appFile]);
            } else if (files.length > 0) {
                // Fallback to first file if no App file found
                setActiveFile(files[0]);
                setOpenFiles([files[0]]);
            }
        } catch (error) {
            console.error('Error fetching project:', error);
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    }, [projectId, navigate]);

    useEffect(() => {
        if (!user) { navigate('/login'); return; }
        fetchProject();
        apiService.getModels().then(({ data }) => setModels(data.models)).catch(console.error);
    }, [user, projectId, navigate, fetchProject]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Chat history functions
    const fetchChatHistory = async () => {
        setLoadingHistory(true);
        try {
            const { data } = await apiService.getChatHistory(projectId);
            setChatHistory(Array.isArray(data) ? data : (data.sessions || data.history || []));
        } catch (error) {
            console.error('Error fetching chat history:', error);
            setChatHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const loadChatSession = async (sessionId) => {
        try {
            setLoadingHistory(true);
            const { data } = await apiService.getChatSession(projectId, sessionId);
            const sessionMessages = data.messages || data || [];
            setMessages(sessionMessages.map(m => ({ role: m.role, content: m.content, type: 'text' })));
            setShowHistory(false);
            setLeftPanelMode('chat'); // Switch to chat view after loading session
        } catch (error) {
            console.error('Error loading chat session:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    // File operations
    const handleFileSelect = (filePath) => {
        if (!openFiles.includes(filePath)) setOpenFiles([...openFiles, filePath]);
        setActiveFile(filePath);
        setViewMode('code');
    };

    const handleCloseFile = (filePath, e) => {
        e?.stopPropagation();
        const newOpenFiles = openFiles.filter(f => f !== filePath);
        setOpenFiles(newOpenFiles);
        if (activeFile === filePath) setActiveFile(newOpenFiles[newOpenFiles.length - 1] || null);
    };

    const handleCodeChange = (value) => {
        if (!activeFile) return;
        setFileContents({ ...fileContents, [activeFile]: value });
        setUnsavedChanges({ ...unsavedChanges, [activeFile]: true });
    };

    const handleCreateFile = () => {
        const fileName = prompt('Enter file name (e.g., Component.jsx):');
        if (!fileName) return;
        setFileContents({ ...fileContents, [fileName]: '' });
        setOpenFiles([...openFiles, fileName]);
        setActiveFile(fileName);
        setUnsavedChanges({ ...unsavedChanges, [fileName]: true });
        setViewMode('code');
    };

    // Rename file
    const handleRenameFile = async (oldName, newName) => {
        if (!oldName || !newName || oldName === newName) return;
        if (fileContents[newName]) {
            alert(`Fișierul ${newName} există deja!`);
            return;
        }

        // Create new file contents with renamed file
        const newFileContents = {};
        for (const [name, content] of Object.entries(fileContents)) {
            if (name === oldName) {
                newFileContents[newName] = content;
            } else {
                newFileContents[name] = content;
            }
        }
        setFileContents(newFileContents);

        // Update open files
        setOpenFiles(prev => prev.map(f => f === oldName ? newName : f));

        // Update active file if it was renamed
        if (activeFile === oldName) {
            setActiveFile(newName);
        }

        // Save all files to backend using updateProject
        try {
            await apiService.updateProject(projectId, { files: newFileContents });
            console.log('File renamed successfully:', oldName, '->', newName);
        } catch (error) {
            console.error('Error renaming file:', error);
            alert('Eroare la redenumirea fișierului');
        }
    };

    // Delete file
    const handleDeleteFile = async (fileName) => {
        if (!fileName) return;

        // Remove from file contents
        const newFileContents = { ...fileContents };
        delete newFileContents[fileName];
        setFileContents(newFileContents);

        // Remove from open files
        const newOpenFiles = openFiles.filter(f => f !== fileName);
        setOpenFiles(newOpenFiles);

        // Update active file if it was deleted
        if (activeFile === fileName) {
            setActiveFile(newOpenFiles.length > 0 ? newOpenFiles[newOpenFiles.length - 1] : null);
        }

        // Save all files to backend using updateProject (this removes the deleted file)
        try {
            await apiService.updateProject(projectId, { files: newFileContents });
            console.log('File deleted successfully:', fileName);
        } catch (error) {
            console.error('Error deleting file:', error);
            alert('Eroare la ștergerea fișierului');
        }
    };

    const saveAllFiles = async (filesToSave = fileContents) => {
        if (Object.keys(filesToSave).length === 0) {
            console.log('No files to save');
            return;
        }
        
        setSaving(true);
        try {
            for (const [path, content] of Object.entries(filesToSave)) {
                await apiService.updateFile(projectId, path, content);
            }
            setUnsavedChanges({});
            setSaveStatus('saved');
            setPreviewKey(prev => prev + 1);
            const { data } = await apiService.getProject(projectId);
            setProject(data);
            setTimeout(() => setSaveStatus(null), 3000);
        } catch (error) {
            console.error('Error saving files:', error);
            // Don't show error status, just log it
        } finally {
            setSaving(false);
        }
    };

    // File attachments
    const handleFileAttachment = async (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = [];
        for (const file of files) {
            const reader = new FileReader();
            const content = await new Promise((resolve) => {
                reader.onload = (e) => resolve(e.target.result);
                file.type.startsWith('image/') ? reader.readAsDataURL(file) : reader.readAsText(file);
            });
            newAttachments.push({ name: file.name, type: file.type, content, isImage: file.type.startsWith('image/') });
        }
        setAttachedFiles(prev => [...prev, ...newAttachments]);
        e.target.value = '';
    };

    // Send message
    const handleSendMessage = async () => {
        if ((!inputMessage.trim() && attachedFiles.length === 0) || generating) return;

        // Prepare message content - include text description of attachments for context
        let messageContent = inputMessage;
        if (attachedFiles.length > 0) {
            const attachmentInfo = attachedFiles.map(f => {
                if (f.isImage) {
                    return `[Image attached: ${f.name}]`;
                } else {
                    return `[File: ${f.name}]\n\`\`\`\n${f.content}\n\`\`\``;
                }
            });
            messageContent = messageContent + '\n\n' + attachmentInfo.join('\n');
        }

        // Prepare attachments for API (include full data for images)
        const attachmentsForApi = attachedFiles.map(f => ({
            name: f.name,
            type: f.type,
            content: f.content,
            isImage: f.isImage
        }));

        const userMessage = { 
            role: 'user', 
            content: messageContent,
            attachments: attachmentsForApi
        };
        
        setMessages(prev => [...prev, { 
            role: 'user', 
            content: inputMessage, 
            type: 'text', 
            attachments: attachedFiles.map(f => ({ name: f.name, type: f.type, content: f.isImage ? f.content : null, isImage: f.isImage })) 
        }]);
        setInputMessage('');
        setAttachedFiles([]);
        setGenerating(true);

        try {
            const { data } = await apiService.generateCode({
                project_id: projectId,
                messages: [
                    ...messages.filter(m => m.type !== 'status' && m.type !== 'plan').map(m => ({ 
                        role: m.role, 
                        content: m.content,
                        attachments: m.attachments || []
                    })), 
                    userMessage
                ],
                model: selectedModel,
                context_files: fileContents,
                thinking_enabled: thinkingEnabled
            });

            // If images were saved, update local file contents
            if (data.saved_images && data.saved_images.length > 0) {
                const newFiles = { ...fileContents };
                for (const img of data.saved_images) {
                    // The image was saved in backend, we need to track it
                    // Note: actual content is in attachedFiles which we already cleared
                    // We'll reload from server on next fetch
                }
                // Reload project to get updated files including saved images
                await fetchProject();
            }

            // Check if response contains plan indicators
            const planIndicators = [
                'Așteaptă confirmarea', 'Asteapta confirmarea',
                'PLAN DE IMPLEMENTARE', 'Plan de implementare', 'Plan de Implementare',
                '## 📋 PLAN', '## Plan', '### Plan',
                'Presupuneri:', 'Presupuneri :', 'Assumptions:',
                'Pași de implementare', 'Pasi de implementare', 'Steps:',
                'Voi implementa', 'Voi crea', 'Voi genera',
                'confirmarea ta', 'confirmă planul', 'confirma planul',
                'Întâi vreau să', 'Intai vreau sa',
                'Înainte de a genera', 'Inainte de a genera'
            ];
            
            const isWaitingForConfirmation = planIndicators.some(indicator => 
                data.response.toLowerCase().includes(indicator.toLowerCase())
            );
            const hasCodeFiles = /```filename:[\w\/\.]+\n[\s\S]*?```/.test(data.response) || 
                                 /```(jsx|javascript|tsx|js)\n[\s\S]*?(const App|function App|export default)[\s\S]*?```/.test(data.response);

            // If no code extracted and response looks like a plan or description, show confirm buttons
            const extractedFiles = extractCodeFromResponse(data.response);
            const hasExtractedCode = Object.keys(extractedFiles).length > 0;

            if ((isWaitingForConfirmation || !hasExtractedCode) && !hasCodeFiles && data.response.length < 5000) {
                // This is a plan or description - show confirmation buttons
                setPendingPlan(data.response.trim());
                setEditablePlan(data.response.trim());
                setMessages(prev => [...prev, { role: 'assistant', type: 'plan', content: data.response.trim() }]);
            } else if (hasExtractedCode) {
                const newFileContents = { ...fileContents, ...extractedFiles };
                setFileContents(newFileContents);
                const newFilePaths = Object.keys(extractedFiles);
                setOpenFiles(prev => [...new Set([...prev, ...newFilePaths])]);
                if (newFilePaths.length > 0) setActiveFile(newFilePaths[0]);
                await saveAllFiles(newFileContents);
                setMessages(prev => [...prev, { role: 'assistant', type: 'status', content: `✓ Generated ${newFilePaths.length} file(s): ${newFilePaths.join(', ')}`, files: newFilePaths }]);
                setViewMode('preview');
                setPreviewKey(prev => prev + 1);
            } else {
                // Show as regular text but with confirm option
                setMessages(prev => [...prev, { role: 'assistant', type: 'text', content: data.response }]);
            }
        } catch (error) {
            console.error('Error generating code:', error);
            const errorContent = createErrorContent(
                error.response?.data?.detail || 'Failed to generate code',
                process.env.REACT_APP_BACKEND_URL
            );
            setMessages(prev => [...prev, { role: 'assistant', type: 'error', content: errorContent }]);
        } finally {
            setGenerating(false);
        }
    };

    // Plan confirmation
    const handleConfirmPlan = async () => {
        const planContent = editablePlan || pendingPlan;
        if (!planContent || generating) return;
        
        setGenerating(true);
        setMessages(prev => [...prev, { role: 'user', type: 'text', content: `[PLAN CONFIRMAT]\n\n${planContent}` }]);
        setPendingPlan(null);
        setEditablePlan('');

        try {
            const { data } = await apiService.generateCode({
                project_id: projectId,
                messages: [...messages.filter(m => m.type !== 'status' && m.type !== 'plan').map(m => ({ role: m.role, content: m.content })), { role: 'user', content: `[PLAN CONFIRMAT]\n\n${planContent}` }],
                model: selectedModel,
                context_files: fileContents,
                thinking_enabled: thinkingEnabled
            });

            const extractedFiles = extractCodeFromResponse(data.response);
            if (Object.keys(extractedFiles).length > 0) {
                const newFileContents = { ...fileContents, ...extractedFiles };
                setFileContents(newFileContents);
                const newFilePaths = Object.keys(extractedFiles);
                setOpenFiles(prev => [...new Set([...prev, ...newFilePaths])]);
                if (newFilePaths.length > 0) setActiveFile(newFilePaths[0]);
                await saveAllFiles(newFileContents);
                setMessages(prev => [...prev, { role: 'assistant', type: 'status', content: `✓ Generated ${newFilePaths.length} file(s): ${newFilePaths.join(', ')}`, files: newFilePaths }]);
                setViewMode('preview');
                setPreviewKey(prev => prev + 1);
            } else {
                setMessages(prev => [...prev, { role: 'assistant', type: 'text', content: data.response }]);
            }
        } catch (error) {
            console.error('Error:', error);
            const errorContent = createErrorContent(
                error.response?.data?.detail || 'Failed',
                process.env.REACT_APP_BACKEND_URL
            );
            setMessages(prev => [...prev, { role: 'assistant', type: 'error', content: errorContent }]);
        } finally {
            setGenerating(false);
        }
    };

    const handleCancelPlan = () => {
        setPendingPlan(null);
        setEditablePlan('');
        setMessages(prev => prev.filter(m => m.type !== 'plan'));
    };

    // Download project
    const handleDownload = async () => {
        if (Object.keys(fileContents).length === 0) { alert('No files to download!'); return; }
        setDownloading(true);
        try {
            const zip = new JSZip();
            Object.entries(fileContents).forEach(([filename, content]) => zip.file(filename, content));
            zip.file('README.md', `# ${project?.name || 'Project'}\n\nGenerated with Mersi.ai\n\n## Files\n${Object.keys(fileContents).map(f => `- ${f}`).join('\n')}`);
            if (!fileContents['package.json']) {
                zip.file('package.json', JSON.stringify({ name: project?.name?.toLowerCase().replace(/\s+/g, '-') || 'app', version: '1.0.0', private: true, dependencies: { 'react': '^18.2.0', 'react-dom': '^18.2.0' }, scripts: { start: 'react-scripts start', build: 'react-scripts build' } }, null, 2));
            }
            const blob = await zip.generateAsync({ type: 'blob' });
            saveAs(blob, `${project?.name?.toLowerCase().replace(/\s+/g, '-') || 'project'}.zip`);
        } catch (error) {
            console.error('Error downloading:', error);
            alert('Download failed: ' + error.message);
        } finally {
            setDownloading(false);
        }
    };

    // Open in Browser - opens generated app in new tab
    const handleOpenInBrowser = () => {
        if (Object.keys(fileContents).length === 0) {
            alert('Nu există fișiere de previzualizat!');
            return;
        }

        // Search for App.jsx/App.js in multiple possible locations
        const appPaths = [
            'App.jsx', 'App.js',
            'src/App.jsx', 'src/App.js',
            'frontend/src/App.jsx', 'frontend/src/App.js'
        ];
        
        let appContent = '';
        for (const path of appPaths) {
            if (fileContents[path]) {
                appContent = fileContents[path];
                break;
            }
        }
        
        if (!appContent) {
            alert('Nu s-a găsit fișierul App.jsx sau App.js!');
            return;
        }
        
        // Check for complex app patterns that can't be previewed in browser
        // (apps that import from separate context/component files)
        const hasAliasImports = appContent.includes("from '@/") || appContent.includes("from \"@/");
        const hasContextImports = appContent.includes("from './context") || 
                                  appContent.includes("from \"./context") ||
                                  appContent.includes("from '../context") ||
                                  appContent.includes("from \"../context") ||
                                  appContent.includes("from './components") ||
                                  appContent.includes("from \"./components") ||
                                  appContent.includes("from '../components") ||
                                  appContent.includes("from \"../components");
        
        // Show complex app message ONLY if app imports from local modules
        // (if app has everything in App.jsx, it will work even with separate files in project)
        if (hasAliasImports || hasContextImports) {
            const complexAppHtml = `<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: system-ui, -apple-system, sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #fffbeb; color: #92400e; }
        .container { text-align: center; padding: 20px; max-width: 450px; }
        .icon { font-size: 48px; margin-bottom: 16px; color: #b45309; }
        h3 { margin: 0 0 8px; color: #b45309; }
        p { margin: 8px 0; font-size: 14px; line-height: 1.5; }
        .tip { margin-top: 16px; padding: 12px; background: #fef3c7; border-radius: 8px; font-size: 13px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
                <path d="m3.3 7 8.7 5 8.7-5"/>
                <path d="M12 22V12"/>
            </svg>
        </div>
        <h3>Complex Application Detected</h3>
        <p>This application uses module imports (contexts, aliases) that cannot be resolved in the live preview.</p>
        <div class="tip"><strong>Tip:</strong> Deploy the application to see the full preview, or download the code and run it locally with <code>npm start</code>.</div>
    </div>
</body>
</html>`;
            const blob = new Blob([complexAppHtml], { type: 'text/html' });
            window.open(URL.createObjectURL(blob), '_blank');
            return;
        }
        
        // Simple clean function - only removes imports and exports
        const cleanCode = (code) => {
            return code
                // Remove all import statements (single and multi-line)
                .replace(/import\s+.*?\s+from\s+['"][^'"]+['"];?\s*/g, '')
                .replace(/import\s*\{[\s\S]*?\}\s*from\s*['"][^'"]+['"];?\s*/g, '')
                .replace(/import\s+['"][^'"]+['"];?\s*/g, '')
                // Remove exports
                .replace(/export\s+default\s+/g, '')
                .replace(/export\s+const\s+/g, 'const ')
                .replace(/export\s+function\s+/g, 'function ')
                .replace(/export\s+class\s+/g, 'class ')
                .split('\n')
                .filter(line => !line.trim().startsWith('export {') && !line.trim().startsWith('export *'))
                .join('\n');
        };
        
        const cleanedAppContent = cleanCode(appContent);
        
        // Detect the main component name with better handling
        let componentName = 'App';
        
        // Try different patterns to find the component name
        const patterns = [
            /export\s+default\s+function\s+([A-Z][a-zA-Z0-9]*)\s*\(/,  // export default function ComponentName(
            /export\s+default\s+class\s+([A-Z][a-zA-Z0-9]*)/,          // export default class ComponentName
            /export\s+default\s+([A-Z][a-zA-Z0-9]*)\s*;?\s*$/m,        // export default ComponentName (at end)
            /const\s+([A-Z][a-zA-Z0-9]*)\s*=.*\nexport\s+default\s+\1/ // const X = ...; export default X
        ];
        
        for (const pattern of patterns) {
            const match = appContent.match(pattern);
            if (match && match[1]) {
                componentName = match[1];
                break;
            }
        }
        
        // If still 'App', check if App is actually defined
        if (componentName === 'App' && !appContent.includes('const App') && !appContent.includes('function App')) {
            // Look for any component definition
            const anyComponentMatch = appContent.match(/const\s+([A-Z][a-zA-Z0-9]*)\s*=\s*\(?.*=>|function\s+([A-Z][a-zA-Z0-9]*)\s*\(/);
            if (anyComponentMatch) {
                componentName = anyComponentMatch[1] || anyComponentMatch[2] || 'App';
            }
        }
        
        // Extract all component/function names defined in the app code to avoid conflicts with mocks
        const definedNames = new Set();
        const definitionPatterns = [
            /const\s+([A-Z][a-zA-Z0-9]*)\s*=/g,
            /function\s+([A-Z][a-zA-Z0-9]*)\s*\(/g,
            /class\s+([A-Z][a-zA-Z0-9]*)\s*/g
        ];
        
        for (const pattern of definitionPatterns) {
            let match;
            while ((match = pattern.exec(appContent)) !== null) {
                definedNames.add(match[1]);
            }
        }
        
        // List of icon names that might conflict
        const iconNames = ['Save', 'Download', 'Trash2', 'Plus', 'Edit', 'Check', 'X', 'Search', 
                          'Menu', 'ChevronDown', 'ChevronUp', 'ChevronLeft', 'ChevronRight',
                          'ArrowLeft', 'ArrowRight', 'Loader2', 'User', 'Settings', 'Home',
                          'Mail', 'Phone', 'Calendar', 'Clock', 'Star', 'Heart', 'Share', 
                          'Copy', 'ExternalLink'];
        
        // Generate icon mocks only for names NOT defined in the app
        const iconMocks = iconNames
            .filter(name => !definedNames.has(name))
            .map(name => `const ${name} = createIcon('${name}');`)
            .join('\n        ');
        
        // Generate the HTML with all mocks
        const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${project?.name || 'Preview'} - Generated by Mersi.ai</title>
    <script src="https://unpkg.com/react@18/umd/react.development.js" crossorigin></script>
    <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js" crossorigin></script>
    <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
        body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; }
        #root { min-height: 100vh; }
        .error-container { padding: 20px; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; margin: 20px; }
    </style>
</head>
<body>
    <div id="root"></div>
    <script type="text/babel">
        const { useState, useEffect, useRef, useCallback, useMemo, useContext, createContext } = React;
        
        // Mock react-router-dom hooks
        const useNavigate = () => (path) => console.log('Navigate to:', path);
        const useParams = () => ({});
        const useSearchParams = () => [new URLSearchParams(), () => {}];
        const Link = ({ to, children, ...props }) => React.createElement('a', { href: to, onClick: (e) => { e.preventDefault(); console.log('Link to:', to); }, ...props }, children);
        const BrowserRouter = ({ children }) => children;
        const Routes = ({ children }) => children;
        const Route = ({ element }) => element;
        
        // Mock other common libraries
        const toast = { success: (msg) => alert(msg), error: (msg) => alert(msg), info: (msg) => alert(msg) };
        const axios = { 
            get: (url) => Promise.resolve({ data: [] }), 
            post: (url, data) => Promise.resolve({ data }),
            put: (url, data) => Promise.resolve({ data }),
            delete: (url) => Promise.resolve({ data: {} })
        };
        
        // Mock lucide-react icons as simple spans (only for icons not defined in app)
        const createIcon = (name) => (props) => React.createElement('span', { ...props, style: { display: 'inline-block', width: '1em', height: '1em' } }, '⬜');
        ${iconMocks}

        // App code (cleaned from imports/exports)
${cleanedAppContent.split('\n').map(line => '        ' + line).join('\n')}
        
        // Find and render the main component
        const AppComponent = typeof ${componentName} !== 'undefined' ? ${componentName} : 
                             typeof App !== 'undefined' ? App :
                             () => React.createElement('div', { className: 'p-8 text-center text-red-500' }, 'No component found');
        
        try {
            const root = ReactDOM.createRoot(document.getElementById('root'));
            root.render(React.createElement(AppComponent));
        } catch (error) {
            document.getElementById('root').innerHTML = '<div class="error-container"><h2>Render Error</h2><pre>' + error.message + '</pre></div>';
            console.error('Render error:', error);
        }
    </script>
</body>
</html>`;

        // Open in new tab
        const blob = new Blob([htmlContent], { type: 'text/html' });
        window.open(URL.createObjectURL(blob), '_blank');
    };

    // Deploy to EC2
    const handleDeploy = async () => {
        setDeploying(true);
        try {
            await saveAllFiles();
            const { data } = await apiService.deployProject(projectId);
            setProject({ ...project, deployment_status: 'deployed', deployment_url: data.url });
            
            // Show deployment success modal
            const deployUrl = data.url;
            const modalId = 'deploy-success-modal';
            
            // Remove existing modal if any
            const existingModal = document.getElementById(modalId);
            if (existingModal) existingModal.remove();
            
            // Create modal
            const modal = document.createElement('div');
            modal.id = modalId;
            modal.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;';
            
            modal.innerHTML = `
                <div style="background: white; border-radius: 12px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); max-width: 90%; max-height: 90%; overflow: auto;">
                    <div style="font-family: system-ui, -apple-system, sans-serif; padding: 20px; max-width: 500px;">
                        <h2 style="margin: 0 0 16px; color: #16a34a; display: flex; align-items: center; gap: 8px;">
                            🚀 Deployment Started!
                        </h2>
                        <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
                            <p style="margin: 0 0 8px; font-weight: 600;">Your application URL:</p>
                            <a href="${deployUrl}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; font-size: 16px; word-break: break-all;">${deployUrl}</a>
                        </div>
                        <div style="background: #fef3c7; border: 1px solid #fde68a; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
                            <p style="margin: 0 0 8px; font-weight: 600;">⏳ Please wait 7-10 minutes</p>
                            <p style="margin: 0; font-size: 14px; color: #78350f;">The server is installing dependencies and building your application.</p>
                        </div>
                        <div style="background: #fee2e2; border: 1px solid #fecaca; border-radius: 8px; padding: 16px;">
                            <p style="margin: 0 0 8px; font-weight: 600;">⚠️ Important:</p>
                            <p style="margin: 0; font-size: 14px; color: #991b1b;">Use <strong>HTTP</strong> (not HTTPS) to access your application.</p>
                        </div>
                    </div>
                    <div style="padding: 0 20px 20px; text-align: right;">
                        <button id="deploy-modal-close-btn" style="background: #2563eb; color: white; border: none; padding: 10px 24px; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500;">OK</button>
                    </div>
                </div>
            `;
            
            document.body.appendChild(modal);
            
            // Add click handler for close button
            document.getElementById('deploy-modal-close-btn').addEventListener('click', () => {
                modal.remove();
            });
            
            // Also close on backdrop click
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    modal.remove();
                }
            });
            
        } catch (error) {
            console.error('Error deploying:', error);
            alert('Deployment failed: ' + (error.response?.data?.detail || error.message));
        } finally {
            setDeploying(false);
        }
    };

    // Finalize project - generate README.md report
    const handleFinalize = async () => {
        if (Object.keys(fileContents).length === 0) {
            alert('Nu există fișiere de finalizat. Generează mai întâi cod!');
            return;
        }

        setFinalizing(true);
        try {
            await saveAllFiles();
            const { data } = await apiService.finalizeProject(projectId);
            
            // Update local file contents with the new README.md and package.json
            const updatedFiles = { 
                ...fileContents, 
                'README.md': data.report 
            };
            
            // Fetch updated project to get package.json
            const { data: updatedProject } = await apiService.getProject(projectId);
            if (updatedProject.files) {
                if (updatedProject.files['package.json']) {
                    updatedFiles['package.json'] = updatedProject.files['package.json'];
                }
            }
            
            setFileContents(updatedFiles);
            
            // Add generated files to open files
            const newFiles = data.files_generated || ['README.md', 'package.json'];
            const filesToOpen = newFiles.filter(f => updatedFiles[f] && !openFiles.includes(f));
            if (filesToOpen.length > 0) {
                setOpenFiles(prev => [...prev, ...filesToOpen]);
            }
            
            setFinalizationReport(data.report);
            setShowFinalizationModal(true);
            
            // Refresh preview
            setPreviewKey(prev => prev + 1);
            
            // Add finalization message to chat
            const depsCount = data.dependencies_detected || 0;
            setMessages(prev => [...prev, {
                role: 'assistant',
                type: 'status',
                content: `✅ Proiect finalizat! README.md și package.json au fost generate și salvate automat.${depsCount > 0 ? ` (${depsCount} dependențe detectate)` : ''}`,
                files: newFiles.filter(f => updatedFiles[f])
            }]);
        } catch (error) {
            console.error('Error finalizing:', error);
            alert('Finalizare eșuată: ' + (error.response?.data?.detail || error.message));
        } finally {
            setFinalizing(false);
        }
    };

    // Render message
    const renderMessage = (message, index) => {
        const isUser = message.role === 'user';

        if (message.type === 'status') {
            return (
                <div key={index} className="flex items-start gap-2 mb-3 p-3 bg-green-50 border border-green-200 rounded-md">
                    <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-sm text-green-800">{message.content}</p>
                        {message.files && (
                            <div className="flex flex-wrap gap-1 mt-2">
                                {message.files.map(file => (
                                    <button key={file} onClick={() => handleFileSelect(file)} className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors">
                                        {file}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (message.type === 'error') {
            return (
                <div key={index} className="flex items-start gap-2 mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-800 prose prose-sm max-w-none prose-a:text-blue-600 prose-a:underline">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </div>
                </div>
            );
        }

        if (message.type === 'plan') {
            return (
                <div key={index} className="mb-4 w-full" data-testid="plan-container">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-500">
                            <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-base font-bold text-purple-700">📋 Plan de Implementare</span>
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Editabil</span>
                            </div>
                            <div className="bg-purple-50 border-2 border-purple-400 rounded-xl p-4 shadow-sm">
                                <textarea
                                    defaultValue={message.content}
                                    onChange={(e) => setEditablePlan(e.target.value)}
                                    onFocus={() => { if (!editablePlan) setEditablePlan(message.content); }}
                                    className="w-full min-h-[280px] bg-white border-2 border-purple-300 rounded-lg p-4 text-sm text-gray-800 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="Editează planul aici..."
                                    data-testid="editable-plan-textarea"
                                />
                                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t-2 border-purple-200">
                                    <button 
                                        onClick={() => {
                                            if (!editablePlan) setEditablePlan(message.content);
                                            handleConfirmPlan();
                                        }} 
                                        disabled={generating} 
                                        className="inline-flex items-center px-3.5 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-md transition-colors shadow-sm" 
                                        data-testid="confirm-plan-btn"
                                    >
                                        {generating && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                                        Confirmă Plan
                                    </button>
                                    <button onClick={handleCancelPlan} disabled={generating} className="inline-flex items-center px-3.5 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 text-sm font-medium rounded-md transition-colors" data-testid="cancel-plan-btn">
                                        Anulează
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div key={index} className={`flex gap-3 mb-4 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-blue-600' : 'bg-gray-100 border border-gray-200'}`}>
                    {isUser ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-gray-600" />}
                </div>
                <div className={`flex flex-col min-w-0 ${isUser ? 'items-end max-w-[85%]' : 'items-start max-w-[95%]'}`}>
                    {/* Show image previews */}
                    {message.attachments?.some(att => att.isImage && att.content) && (
                        <div className="flex flex-wrap gap-2 mb-2">
                            {message.attachments.filter(att => att.isImage && att.content).map((att, i) => (
                                <img 
                                    key={i} 
                                    src={att.content} 
                                    alt={att.name} 
                                    className="max-w-[200px] max-h-[150px] rounded-lg border border-gray-200 object-cover cursor-pointer hover:opacity-90"
                                    onClick={() => window.open(att.content, '_blank')}
                                />
                            ))}
                        </div>
                    )}
                    {/* Show file attachment badges */}
                    {message.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1">
                            {message.attachments.map((att, i) => (
                                <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex items-center gap-1">
                                    {att.isImage || att.type?.startsWith('image/') ? <ImageIcon className="w-3 h-3" /> : <Paperclip className="w-3 h-3" />}
                                    {att.name}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className={`px-4 py-3 rounded-2xl text-sm w-full ${isUser ? 'bg-blue-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md border border-gray-200'}`}>
                        {isUser ? (
                            <p className="whitespace-pre-wrap break-words">{message.content}</p>
                        ) : (
                            <div className="prose prose-sm max-w-none prose-gray [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_.prism-code]:max-w-full">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{message.content}</ReactMarkdown>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Show thinking toggle for supported models
    const showThinkingToggle = ['claude', 'openai', 'qwen', 'glm', 'kimi'].includes(selectedModel);

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col bg-gray-100">
            {/* Header */}
            <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="h-8" data-testid="back-btn">
                        <ArrowLeft className="w-4 h-4 mr-1" />Back
                    </Button>
                    <div className="h-6 w-px bg-gray-200" />
                    <h1 className="font-semibold text-gray-800 truncate max-w-[200px]">{project?.name || 'Loading...'}</h1>
                    {saveStatus === 'saved' && (
                        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                            Salvat
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Select value={selectedModel} onValueChange={setSelectedModel}>
                        <SelectTrigger className="w-[160px] h-8"><SelectValue placeholder="Model" /></SelectTrigger>
                        <SelectContent>
                            {models.map((model) => <SelectItem key={model.id} value={model.id}>{model.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {showThinkingToggle && (
                        <Button variant={thinkingEnabled ? 'default' : 'outline'} size="sm" onClick={() => setThinkingEnabled(!thinkingEnabled)} className={`h-8 ${thinkingEnabled ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`} data-testid="thinking-toggle-btn">
                            <Brain className="w-4 h-4 mr-1" />{thinkingEnabled ? 'Thinking ON' : 'Thinking'}
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading} className="h-8" data-testid="download-btn">
                        {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}Download
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDeploy} disabled={deploying} className="h-8" data-testid="deploy-btn">
                        {deploying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Rocket className="w-4 h-4 mr-1" />}Deploy
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleFinalize} disabled={finalizing} className="h-8 border-green-300 text-green-600 hover:bg-green-50" data-testid="finalize-btn">
                        {finalizing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}Finalizează
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowGitHubModal(true)} className="h-8" data-testid="github-btn">
                        <Github className="w-4 h-4 mr-1" />GitHub
                    </Button>
                </div>
            </header>

            {/* Main content */}
            <div className="flex-1 overflow-hidden">
                <ResizablePanelGroup direction="horizontal">
                    {/* Left Panel - Chat or Code */}
                    <ResizablePanel defaultSize={40} minSize={25}>
                        <div className="h-full flex flex-col bg-white relative">
                            {/* Chat History Overlay */}
                            {showHistory && (
                                <ChatHistoryPanel
                                    chatHistory={chatHistory}
                                    loadingHistory={loadingHistory}
                                    onLoadSession={loadChatSession}
                                    onDeleteSession={(id) => { apiService.deleteChatSession(projectId, id); setChatHistory(prev => prev.filter(s => s.id !== id)); }}
                                    onNewChat={() => { setMessages([]); setShowHistory(false); }}
                                    onClose={() => setShowHistory(false)}
                                />
                            )}

                            {/* Panel mode tabs */}
                            <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
                                <Button variant={leftPanelMode === 'chat' ? 'default' : 'ghost'} size="sm" onClick={() => setLeftPanelMode('chat')} className="h-7">
                                    <MessageSquare className="w-4 h-4 mr-1" />Chat
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => { fetchChatHistory(); setShowHistory(true); }} className="h-7" data-testid="history-btn">
                                    <History className="w-4 h-4 mr-1" />History
                                </Button>
                                <Button variant={leftPanelMode === 'code' ? 'default' : 'ghost'} size="sm" onClick={() => setLeftPanelMode('code')} className="h-7">
                                    <Code2 className="w-4 h-4 mr-1" />Code
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => saveAllFiles(fileContents)} 
                                    disabled={saving || Object.keys(fileContents).length === 0}
                                    className="h-7"
                                    title="Save all files"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                </Button>
                            </div>

                            {leftPanelMode === 'chat' ? (
                                <div className="flex-1 flex flex-col min-h-0">
                                    {/* Messages */}
                                    <div className="flex-1 overflow-y-auto p-4 min-h-0">
                                        {messages.length === 0 ? (
                                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                                <Bot className="w-12 h-12 mb-4 opacity-50" />
                                                <p className="text-sm">Descrie ce vrei să construiești...</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {messages.map((msg, idx) => renderMessage(msg, idx))}
                                                <div ref={messagesEndRef} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Chat input */}
                                    <div className="border-t border-gray-200 p-3 flex-shrink-0">
                                        {attachedFiles.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-2">
                                                {attachedFiles.map((file, index) => (
                                                    <div key={index} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs">
                                                        {file.isImage ? <ImageIcon className="w-3 h-3" /> : <Paperclip className="w-3 h-3" />}
                                                        <span className="max-w-[100px] truncate">{file.name}</span>
                                                        <button onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))} className="ml-1 hover:text-blue-900"><X className="w-3 h-3" /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="flex gap-2 items-end">
                                            <div className="flex-1 relative">
                                                <Textarea
                                                    value={inputMessage}
                                                    onChange={(e) => {
                                                        setInputMessage(e.target.value);
                                                        // Auto-resize textarea
                                                        e.target.style.height = 'auto';
                                                        e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                                                    }}
                                                    onKeyDown={(e) => {
                                                        // Ctrl+Enter sends message, Enter creates new line
                                                        if (e.key === 'Enter' && e.ctrlKey) {
                                                            e.preventDefault();
                                                            handleSendMessage();
                                                        }
                                                    }}
                                                    onPaste={async (e) => {
                                                        // Handle image paste from clipboard
                                                        const items = e.clipboardData?.items;
                                                        if (!items) return;
                                                        
                                                        for (const item of items) {
                                                            if (item.type.startsWith('image/')) {
                                                                e.preventDefault();
                                                                const file = item.getAsFile();
                                                                if (file) {
                                                                    const reader = new FileReader();
                                                                    reader.onload = (event) => {
                                                                        const content = event.target.result;
                                                                        const fileName = `pasted-image-${Date.now()}.${file.type.split('/')[1] || 'png'}`;
                                                                        setAttachedFiles(prev => [...prev, {
                                                                            name: fileName,
                                                                            type: file.type,
                                                                            content,
                                                                            isImage: true
                                                                        }]);
                                                                    };
                                                                    reader.readAsDataURL(file);
                                                                }
                                                                break;
                                                            }
                                                        }
                                                    }}
                                                    placeholder="Descrie ce vrei să construiești..."
                                                    className="min-h-[60px] max-h-[200px] resize-none pr-10 overflow-y-auto"
                                                    style={{ height: '60px' }}
                                                    disabled={generating}
                                                    data-testid="chat-input"
                                                />
                                                <button onClick={() => fileInputRef.current?.click()} className="absolute right-2 bottom-2 p-1.5 text-gray-400 hover:text-gray-600" disabled={generating}>
                                                    <Paperclip className="w-4 h-4" />
                                                </button>
                                                <input ref={fileInputRef} type="file" multiple onChange={handleFileAttachment} className="hidden" accept="image/*,.txt,.js,.jsx,.ts,.tsx,.css,.html,.json,.md" />
                                            </div>
                                            <Button onClick={handleSendMessage} disabled={(!inputMessage.trim() && attachedFiles.length === 0) || generating} className="h-[60px] px-4" data-testid="send-message-btn">
                                                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            </Button>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1 text-center">Ctrl+Enter pentru a trimite • Poți atașa fișiere sau lipi imagini</p>
                                    </div>
                                </div>
                            ) : (
                                <ResizablePanelGroup direction="horizontal" className="flex-1">
                                    <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
                                        <FileTreePanel 
                                            fileContents={fileContents} 
                                            activeFile={activeFile} 
                                            onFileSelect={handleFileSelect} 
                                            onCreateFile={handleCreateFile}
                                            onRenameFile={handleRenameFile}
                                            onDeleteFile={handleDeleteFile}
                                        />
                                    </ResizablePanel>
                                    <ResizableHandle withHandle />
                                    <ResizablePanel defaultSize={75}>
                                        <CodeEditorPanel
                                            activeFile={activeFile}
                                            openFiles={openFiles}
                                            fileContents={fileContents}
                                            unsavedChanges={unsavedChanges}
                                            onFileSelect={handleFileSelect}
                                            onCloseFile={handleCloseFile}
                                            onCodeChange={handleCodeChange}
                                        />
                                    </ResizablePanel>
                                </ResizablePanelGroup>
                            )}
                        </div>
                    </ResizablePanel>

                    <ResizableHandle withHandle />

                    {/* Right Panel - Preview or Code */}
                    <ResizablePanel defaultSize={60} minSize={30}>
                        <div className="h-full flex flex-col bg-white">
                            {/* View mode tabs */}
                            <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
                                <Button variant={viewMode === 'preview' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('preview')} className="h-7">
                                    <Eye className="w-4 h-4 mr-1" />Preview
                                </Button>
                                <Button variant={viewMode === 'code' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('code')} className="h-7">
                                    <Code2 className="w-4 h-4 mr-1" />Code
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => saveAllFiles(fileContents)} 
                                    disabled={saving || Object.keys(fileContents).length === 0}
                                    className="h-7"
                                    title="Save all files"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={handleOpenInBrowser} 
                                    disabled={Object.keys(fileContents).length === 0}
                                    className="ml-auto h-7"
                                    title="Open in Browser"
                                    data-testid="open-in-browser-btn"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </Button>
                            </div>

                            <div className="flex-1 overflow-hidden">
                                {viewMode === 'preview' ? (
                                    <PreviewPanel fileContents={fileContents} previewKey={previewKey} onRefresh={() => setPreviewKey(prev => prev + 1)} />
                                ) : (
                                    <ResizablePanelGroup direction="horizontal" className="h-full">
                                        <ResizablePanel defaultSize={20} minSize={12} maxSize={35}>
                                            <FileTreePanel 
                                                fileContents={fileContents} 
                                                activeFile={activeFile} 
                                                onFileSelect={handleFileSelect} 
                                                onCreateFile={handleCreateFile}
                                                onRenameFile={handleRenameFile}
                                                onDeleteFile={handleDeleteFile}
                                            />
                                        </ResizablePanel>
                                        <ResizableHandle withHandle />
                                        <ResizablePanel defaultSize={80}>
                                            <CodeEditorPanel
                                                activeFile={activeFile}
                                                openFiles={openFiles}
                                                fileContents={fileContents}
                                                unsavedChanges={unsavedChanges}
                                                onFileSelect={handleFileSelect}
                                                onCloseFile={handleCloseFile}
                                                onCodeChange={handleCodeChange}
                                            />
                                        </ResizablePanel>
                                    </ResizablePanelGroup>
                                )}
                            </div>
                        </div>
                    </ResizablePanel>
                </ResizablePanelGroup>
            </div>

            {/* GitHub Modal */}
            {showGitHubModal && (
                <GitHubModal
                    isOpen={showGitHubModal}
                    onClose={() => setShowGitHubModal(false)}
                    projectId={projectId}
                    projectName={project?.name}
                    files={fileContents}
                    onImportSuccess={async (data) => {
                        // Reload project to get imported files
                        setShowGitHubModal(false);
                        await fetchProject();
                    }}
                />
            )}

            {/* Finalization Report Modal */}
            {showFinalizationModal && finalizationReport && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col m-4">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <h2 className="text-lg font-semibold text-gray-800">Raport de Finalizare</h2>
                            </div>
                            <button onClick={() => setShowFinalizationModal(false)} className="p-1 hover:bg-gray-100 rounded">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <ScrollArea className="flex-1 p-4">
                            <div className="prose prose-sm max-w-none">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {finalizationReport}
                                </ReactMarkdown>
                            </div>
                        </ScrollArea>
                        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
                            <Button variant="outline" onClick={() => {
                                const blob = new Blob([finalizationReport], { type: 'text/markdown' });
                                saveAs(blob, `${project?.name?.toLowerCase().replace(/\s+/g, '-') || 'project'}-finalization-report.md`);
                            }}>
                                <Download className="w-4 h-4 mr-1" />
                                Descarcă Raport
                            </Button>
                            <Button onClick={() => setShowFinalizationModal(false)}>
                                Închide
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditorPage;
